import * as React from "react";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { useLingui } from "@lingui/react/macro";
import { Button } from "./button";
import { XIcon } from "lucide-react";

const badgeVariants = cva(
  "inline-flex items-center justify-center border border-transparent font-medium w-fit whitespace-nowrap shrink-0 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        yellow:
          "border-yellow-50 bg-yellow-50 [&_svg]:text-yellow-800 [a&]:hover:bg-yellow-500/90",
        purple:
          "border-purple-50 bg-purple-50 [&_svg]:text-purple-600 [a&]:hover:bg-purple-500/90",
        red: "border-red-50 bg-red-50 [&_svg]:text-red-700 [a&]:hover:bg-red-500/90",
        green:
          "border-green-50 bg-green-50 [&_svg]:text-green-700 [a&]:hover:bg-green-500/90",
        blue: "border-blue-50 bg-blue-50 [&_svg]:text-blue-700 [a&]:hover:bg-blue-500/90",
        violet:
          "border-violet-50 bg-violet-50 text-violet-700 [a&]:hover:bg-violet-500/90",
        cyan: "border-cyan-200 bg-cyan-50 text-cyan-700 [a&]:hover:bg-cyan-500/90",
        fuchsia:
          "border-fuchsia-50 bg-fuchsia-50 [&_svg]:text-fuchsia-700 [a&]:hover:bg-fuchsia-500/90",
        secondary:
          "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "text-primary underline-offset-4 [a&]:hover:underline",
      },
      size: {
        default: "px-2 py-0.5 text-xs [&>svg]:size-3",
        lg: "text-sm h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 [&>svg]:size-4 ",
      },
      shape: {
        round: "rounded-full",
        square: "rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      shape: "round",
    },
    compoundVariants: [
      {
        shape: "square",
        size: "lg",
        className: "rounded-md",
      },
      {
        shape: "square",
        size: "default",
        className: "rounded-sm",
      },
    ],
  }
);

export type BadgeProps = useRender.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    onDelete?: () => void;
  };

function BadgeComponent(
  {
    className,
    variant = "default",
    size = "default",
    shape = "round",
    render,
    children,
    onDelete,
    onClick,
    onDoubleClick,
    onMouseDown,
    ...props
  }: BadgeProps,
  ref: React.Ref<HTMLSpanElement>
) {
  const { t } = useLingui();

  return useRender({
    defaultTagName: "span",
    ref,
    props: mergeProps<"span">(
      {
        className: cn(badgeVariants({ variant, size, shape }), className),
        children: (
          <>
            {onClick ? (
              <button
                type="button"
                onClick={onClick}
                onDoubleClick={onDoubleClick}
                onMouseDown={onMouseDown}
                className="badge-inner"
                tabIndex={-1}
              >
                {children}
              </button>
            ) : (
              children
            )}
            {onDelete && (
              <Button
                aria-label={t`Ta bort`}
                title={t`Ta bort`}
                size="icon-xs"
                variant="ghost"
                onClick={onDelete}
                className="badge-delete transition-none w-min"
                tabIndex={-1}
                type="button"
              >
                <XIcon />
              </Button>
            )}
          </>
        ),
      },
      onClick ? props : { onDoubleClick, onMouseDown, ...props }
    ),
    render,
    state: {
      slot: "badge",
      variant,
      size,
      shape,
    },
  });
}

const Badge = React.forwardRef(BadgeComponent);

export { Badge, badgeVariants };
